/** Town-screen logic that is not geometry: build options, recruiting, income.
 *
 * Pure. Every "why not" string on this screen is derived from a `Reason` the
 * kernel produced - `explainReason` only puts content names where the kernel
 * had to put ids. Nothing here invents an explanation, because a hand-written
 * one drifts the first time a rule changes and then lies to the player.
 */

import type {
  ArmyState, ArtifactDef, ArtifactDefId, BuildingDef, BuildingDefId, ContentRegistry,
  CreatureDef, CreatureDefId, HeroState, Modifier, Reason, ResourceKind, Resources,
  SpellDef, SpellDefId, TownState,
} from "@game/core";
import { RESOURCES, poolKeyFor, reasonText, resolveStat } from "@game/core";
import { affordableCount, canAfford, titleCase } from "./format.js";

// ------------------------------------------------------------------ reasons

const nameOfBuilding = (content: ContentRegistry, id: BuildingDefId): string =>
  content.buildings.get(id)?.name ?? titleCase(id as string);

const nameOfCreature = (content: ContentRegistry, id: CreatureDefId): string =>
  content.creatures.get(id)?.namePlural ?? titleCase(id as string);

const RESOURCE_LABEL: Readonly<Record<ResourceKind, string>> = Object.freeze({
  gold: "gold", wood: "wood", ore: "ore", mercury: "mercury",
  sulfur: "sulfur", crystal: "crystal", gems: "gems",
});

/**
 * A `Reason` as a sentence, with ids replaced by the names the player sees.
 *
 * Falls through to the kernel's own `reasonText` for every code that carries no
 * id, so a reason added to the union later still renders - as the kernel's
 * wording rather than as nothing.
 */
export function explainReason(content: ContentRegistry, r: Reason): string {
  switch (r.code) {
    case "MISSING_PREREQUISITE":
      return `Requires ${nameOfBuilding(content, r.building)}.`;
    case "ALREADY_BUILT":
      return `${nameOfBuilding(content, r.building)} is already built.`;
    case "INSUFFICIENT_RESOURCES":
      return `Not enough ${RESOURCE_LABEL[r.resource]}: ${r.have} of ${r.need}.`;
    case "NONE_AVAILABLE":
      return `Only ${r.have} ${nameOfCreature(content, r.creature)} available.`;
    case "BUILD_LIMIT_REACHED":
      return "This town has already built today.";
    case "WRONG_TERRAIN":
      return "That structure does not belong to this town.";
    default:
      return reasonText(r);
  }
}

// ------------------------------------------------------------ build options

export interface PrerequisiteLine {
  readonly building: BuildingDef | null;
  readonly id: BuildingDefId;
  readonly met: boolean;
}

export interface BuildOption {
  readonly def: BuildingDef;
  /** Null when the kernel would allow it right now. */
  readonly reason: Reason | null;
  readonly affordable: boolean;
  readonly prerequisites: readonly PrerequisiteLine[];
}

/**
 * What the build dialog shows for one building.
 *
 * `reason` comes from the caller, which got it from the kernel. The
 * prerequisite list is separate because the kernel reports only the *first*
 * unmet one and a player wants to see the whole ladder; `met` is read from the
 * town's built list, which is the same data the kernel tested.
 */
export function buildOption(
  content: ContentRegistry,
  town: TownState,
  treasury: Resources,
  def: BuildingDef,
  reason: Reason | null,
): BuildOption {
  const built = new Set<string>(town.built.map((b) => b as string));
  const ids: BuildingDefId[] = [...def.requires];
  if (def.upgradeOf !== null && !ids.includes(def.upgradeOf)) ids.push(def.upgradeOf);
  const prerequisites = ids.map((id) => ({
    id,
    building: content.buildings.get(id) ?? null,
    met: built.has(id as string),
  }));
  return { def, reason, affordable: canAfford(treasury, def.cost), prerequisites };
}

export const unmetPrerequisites = (o: BuildOption): readonly PrerequisiteLine[] =>
  o.prerequisites.filter((p) => !p.met);

// --------------------------------------------------------------- recruiting

export interface RecruitOption {
  readonly creature: CreatureDef;
  /** The kernel's `availableOf`, by way of the host: own pool plus the base form's. */
  readonly available: number;
  readonly unitCost: Partial<Resources>;
  /** Capped by the pool, by the treasury and by the free army slots. */
  readonly maxCount: number;
}

/**
 * The kernel's `availableOf` for a caller that holds content and a town but
 * no `Ctx`: the creature's own pool plus the base form's, with the kernel's
 * own `poolKeyFor` deciding which pool that is, so the two never disagree on
 * it. The town screen itself is handed the kernel's count by the host; this
 * remains for the dwelling strip, which reads a town straight.
 */
export function availableOf(
  content: ContentRegistry, town: TownState, def: CreatureDefId,
): number {
  const direct = town.available[def as string] ?? 0;
  const key = poolKeyFor(content, def);
  return key === (def as string) ? direct : direct + (town.available[key] ?? 0);
}

/** Free slots for `def`: an existing stack of the same creature counts too. */
export function roomFor(army: ArmyState | null, def: CreatureDefId): boolean {
  if (army === null) return false;
  for (const s of army.slots) if (s === null || s.def === def) return true;
  return false;
}

/** `available` is the kernel's count for this form, see `TownScreenProps.available`. */
export function recruitOption(
  treasury: Resources,
  army: ArmyState | null,
  creature: CreatureDef,
  available: number,
): RecruitOption {
  const byPurse = affordableCount(treasury, creature.cost, available);
  return {
    creature,
    available,
    unitCost: creature.cost,
    maxCount: roomFor(army, creature.id) ? byPurse : 0,
  };
}

/** The creatures a town's built dwellings offer, in tier order. */
export function recruitableCreatures(
  content: ContentRegistry, town: TownState,
): readonly CreatureDef[] {
  const seen = new Set<string>();
  const out: CreatureDef[] = [];
  for (const id of [...town.built].sort()) {
    const b = content.buildings.get(id);
    if (b === undefined || b.dwellingFor === null) continue;
    if (seen.has(b.dwellingFor as string)) continue;
    seen.add(b.dwellingFor as string);
    const c = content.creatures.get(b.dwellingFor);
    if (c !== undefined) out.push(c);
  }
  out.sort((a, b) =>
    a.tier - b.tier
    || Number(a.upgraded) - Number(b.upgraded)
    || ((a.id as string) < (b.id as string) ? -1 : 1));
  return out;
}

// ------------------------------------------------------------------ income

const INCOME_STAT: Readonly<Record<ResourceKind, string>> = Object.freeze({
  gold: "income.gold", wood: "income.wood", ore: "income.ore",
  mercury: "income.mercury", sulfur: "income.sulfur",
  crystal: "income.crystal", gems: "income.gems",
});

/**
 * A town's daily income, resolved the way the kernel resolves it.
 *
 * The hall chain uses `max`, so City Hall outbids Town Hall instead of stacking
 * with it; that behaviour lives in `resolveStat`, which is why this reads the
 * modifiers rather than summing values. A screen that summed would show 3500
 * gold for a Capitol town and be wrong by 3000.
 */
export function townIncome(content: ContentRegistry, town: TownState): Resources {
  const mods: Modifier[] = [];
  for (const id of [...town.built].sort()) {
    const b = content.buildings.get(id);
    if (b === undefined) continue;
    for (const m of b.modifiers) mods.push(m);
  }
  for (const m of town.modifiers) mods.push(m);

  const out: Record<string, number> = {};
  for (const kind of RESOURCES) {
    const stat = INCOME_STAT[kind];
    const v = resolveStat(0, mods.filter((m) => (m.stat as string) === stat)).value;
    out[kind] = v < 0 ? 0 : v;
  }
  return out as unknown as Resources;
}

export const addResources = (a: Resources, b: Resources): Resources => {
  const out: Record<string, number> = {};
  for (const kind of RESOURCES) out[kind] = a[kind] + b[kind];
  return out as unknown as Resources;
};

export const NO_RESOURCES: Resources = Object.freeze({
  gold: 0, wood: 0, ore: 0, mercury: 0, sulfur: 0, crystal: 0, gems: 0,
});

// -------------------------------------------------------------- mage guild

export interface GuildLevelView {
  readonly level: number;
  readonly spells: readonly SpellDef[];
  /** Levels the guild has not been built up to are shown as locked shelves. */
  readonly built: boolean;
}

export const guildLevelOf = (content: ContentRegistry, town: TownState): number => {
  let lvl = 0;
  for (const id of town.built) {
    const b = content.buildings.get(id);
    if (b !== undefined && b.guildLevel > lvl) lvl = b.guildLevel;
  }
  return lvl;
};

export function guildView(content: ContentRegistry, town: TownState): readonly GuildLevelView[] {
  const built = guildLevelOf(content, town);
  const out: GuildLevelView[] = [];
  for (let level = 1; level <= 5; level++) {
    const ids: readonly SpellDefId[] = town.guildSpells[level - 1] ?? [];
    const spells: SpellDef[] = [];
    for (const id of ids) {
      const def = content.spells.get(id);
      if (def !== undefined) spells.push(def);
    }
    spells.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    out.push({ level, spells, built: level <= built });
  }
  return out;
}

// -------------------------------------------------------------- the counter

/**
 * What a town sells over the counter, and whether this hero can take it.
 *
 * The stock is `sells` off every built building, which is the same list the
 * kernel's `townShopStock` builds - and it is derived here rather than handed
 * over because it is a fact about *content*, not a rule: a building's `sells`
 * is data the screen may read, the same way it reads a building's cost. The
 * legality is the opposite and is not derived: `reason` comes from the kernel
 * through `checkCommand`, because whether this hero may buy this machine
 * depends on his slots, his backpack and his purse.
 */
export interface ShopOption {
  readonly artifact: ArtifactDefId;
  readonly def: ArtifactDef | null;
  readonly cost: Resources;
  readonly reason: Reason | null;
  readonly affordable: boolean;
  /** Already worn, so the counter shows it as owned rather than as refused. */
  readonly owned: boolean;
}

export function shopStock(
  content: ContentRegistry, town: TownState,
): readonly ArtifactDefId[] {
  const out: ArtifactDefId[] = [];
  for (const id of town.built) {
    const b = content.buildings.get(id);
    if (b === undefined) continue;
    for (const a of b.sells) if (!out.includes(a)) out.push(a);
  }
  out.sort();
  return out;
}

export function shopOption(
  content: ContentRegistry,
  artifact: ArtifactDefId,
  treasury: Resources,
  hero: HeroState | null,
  reason: Reason | null,
): ShopOption {
  const def = content.artifacts.get(artifact) ?? null;
  const cost: Resources = { ...NO_RESOURCES, gold: def?.cost ?? 0 };
  const owned = hero !== null
    && (hero.backpack.includes(artifact)
      || Object.keys(hero.artifacts).some((k) => hero.artifacts[k] === artifact));
  return {
    artifact, def, cost, reason,
    affordable: canAfford(treasury, cost),
    owned,
  };
}
